import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { createPrintAgent } from '../lib/printAgent.js';

const commOptions = [
  ['USB', 'USB'],
  ['SERIAL', 'Serial'],
  ['TCP_IP', 'TCP/IP'],
  ['BLUETOOTH', 'Bluetooth'],
  ['WEBSOCKET', 'WebSocket'],
  ['WINDOWS_PRINTER', 'Impressora Windows'],
  ['FILE', 'Arquivo'],
];
const cutOptions = [
  ['NONE', 'Não corta'],
  ['EACH_LABEL', 'Cada etiqueta'],
  ['EACH_BATCH', 'Cada lote'],
  ['END_OF_JOB', 'Final do trabalho'],
];
const orientationOptions = [
  ['NORMAL', 'Normal'],
  ['ROTATE_90', '90°'],
  ['ROTATE_180', '180°'],
  ['ROTATE_270', '270°'],
];
const sensorOptions = [
  ['GAP', 'Gap'],
  ['REFLECTIVE', 'Reflexivo'],
  ['CONTINUOUS', 'Contínuo'],
  ['NOTCH', 'Entalhe'],
  ['HOLE', 'Furo'],
  ['RFID', 'RFID'],
];
const incrementOptions = [
  ['APPLICATION', 'Aplicação'],
  ['PRINTER', 'Impressora'],
];
const dataSourceTriggerOptions = [
  ['ON_OPEN', 'Ao abrir'],
  ['ON_PREVIEW', 'Ao visualizar'],
  ['ON_PRINT', 'Ao imprimir'],
  ['MANUAL', 'Manual'],
];
// ponytail: nomes comuns que o diálogo do navegador lista; a seleção real fica no user, entío sugerimos só os nomes.
const commonPrinterNames = ['Microsoft Print to PDF', 'Save as PDF', 'Generic / Text Only'];

const blank = () => ({
  name: '',
  manufacturer: '',
  model: '',
  language: 'ZPL',
  dpi: 203,
  defaultSpeedIps: 3,
  defaultDensity: 10,
  orientation: 'NORMAL',
  horizontalAdjustmentMm: 0,
  verticalAdjustmentMm: 0,
  maxPrintWidthMm: 0,
  sensorType: 'GAP',
  cutMode: 'NONE',
  backfeedDots: 0,
  incrementMode: 'APPLICATION',
  dataSourceTrigger: 'ON_PRINT',
  mirrored: false,
  communicationType: 'WINDOWS_PRINTER',
  rfidSettingsText: '{\n  "chipType": "",\n  "memoryBank": "",\n  "verification": true\n}',
});

function prettifyJson(value) {
  return JSON.stringify(value ?? {}, null, 2);
}

function parseJsonField(label, value) {
  try {
    return JSON.parse(value || '{}');
  } catch {
    throw new Error(`O bloco "${label}" precisa ser um JSON válido.`);
  }
}

function NumberInput({ label, value, step = '1', onChange }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] font-semibold text-on-surface-variant">{label}</span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
      />
    </label>
  );
}

function SelectInput({ label, value, options, onChange }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[10px] font-semibold text-on-surface-variant">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function Printers({ user }) {
  const canManage = user?.role === 'ADMIN' || user?.role === 'DESIGNER';
  const [list, setList] = useState([]);
  const [form, setForm] = useState(blank());
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const agentRef = useRef(null);
  const [agentOnline, setAgentOnline] = useState(false);
  const [agentConnecting, setAgentConnecting] = useState(false);
  const [agentBusy, setAgentBusy] = useState(false);
  const [discovered, setDiscovered] = useState({ windows: [], serial: [], network: [] });
  const [awaitingPrinterName, setAwaitingPrinterName] = useState(false);
  const [capturedName, setCapturedName] = useState('');
  const [pendingPrinterName, setPendingPrinterName] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [viewMode, setViewMode] = useState('card');
  const [detectPage, setDetectPage] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState([]);

  useEffect(() => {
    const agent = createPrintAgent(undefined, { autoReconnect: true });
    agentRef.current = agent;
    const unsubscribe = agent.onChange(setAgentOnline);
    return () => {
      unsubscribe();
      agent.disconnect();
    };
  }, []);

  async function connectAgent() {
    if (agentOnline || agentConnecting) return;
    setError('');
    setStatus('');
    setAgentConnecting(true);
    const connected = await agentRef.current.connect();
    setAgentConnecting(false);
    if (!connected) {
      setError('Nao foi possivel conectar ao Print Agent. Abra o agent local e tente novamente.');
      return;
    }
    await searchAgent();
  }

  async function searchAgent() {
    setError('');
    setStatus('');
    if (!agentRef.current.isOnline() && !(await connectAgent())) return;
    setAgentBusy(true);
    try {
      const agent = agentRef.current;
      const printers = await agent.listPrinters();
      setDiscovered({
        windows: printers.windows || [],
        serial: printers.serial || [],
        network: printers.network || [],
      });
      setDetectPage(0);
    } catch (err) {
      setError(err.message);
    } finally {
      setAgentBusy(false);
    }
  }

  function openBrowserPrintDialog() {
    setError('');
    setStatus('');
    setAwaitingPrinterName(false);
    setCapturedName('');
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write('<!doctype html><html><head><title>Escolher impressora</title></head><body><p>Escolha a impressora de destino.</p></body></html>');
    doc.close();
    const finish = () => {
      iframe.remove();
      setAwaitingPrinterName(true);
      setStatus('Janela fechada. Informe abaixo o nome da impressora que você escolheu.');
    };
    iframe.contentWindow.addEventListener('afterprint', finish);
    setTimeout(() => iframe.contentWindow.print(), 50);
  }

  function confirmCapturedName(e) {
    e.preventDefault();
    const name = capturedName.trim();
    if (!name) {
      setError('Informe o nome da impressora escolhida.');
      return;
    }
    applyDiscovered(name);
    setAwaitingPrinterName(false);
    setCapturedName('');
  }

  function pickPrinterName(name) {
    setPendingPrinterName(name);
  }

  function confirmPendingPrinter() {
    const name = pendingPrinterName;
    setPendingPrinterName(null);
    setAwaitingPrinterName(false);
    openRegisterForm(name, 'WINDOWS_PRINTER');
  }

  function openRegisterForm(name, communicationType) {
    applyDiscovered(name, { communicationType });
    setFormOpen(true);
  }

  function applyDiscovered(name, overrides = {}) {
    setEditingId('');
    setForm({ ...blank(), name, communicationType: 'WINDOWS_PRINTER', ...overrides });
    setStatus(`Campos preenchidos com "${name}". Revise e cadastre.`);
  }

  async function load() {
    try {
      setList(await api.listPrinters());
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const detectRows = [
    ...discovered.windows.map((p) => ({
      key: p.name,
      name: p.name,
      port: p.port,
      comm: 'WINDOWS_PRINTER',
      registered: list.find((r) => r.name === p.name),
    })),
    ...discovered.serial.map((p) => ({
      key: p.path,
      name: p.path,
      port: p.manufacturer || '',
      comm: 'SERIAL',
      registered: null,
      kind: 'serial',
    })),
    ...discovered.network.map((p) => ({
      key: `${p.host}:${p.port}`,
      name: p.host,
      port: `porta ${p.port}`,
      comm: 'TCP_IP',
      registered: null,
      kind: 'network',
    })),
  ];
  const detectPageCount = Math.max(1, Math.ceil(detectRows.length / 5));
  const detectPageRows = detectRows.slice(detectPage * 5, detectPage * 5 + 5);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    setStatus('');
    try {
      const { rfidSettingsText, ...rest } = form;
      const payload = {
        ...rest,
        rfidSettings: parseJsonField('RFID', form.rfidSettingsText),
      };
      if (editingId) {
        await api.updatePrinter(editingId, payload);
        setStatus('Impressora atualizada.');
      } else {
        await api.createPrinter(payload);
        setStatus('Impressora cadastrada.');
      }
      setForm(blank());
      setEditingId('');
      setFormOpen(false);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  const FORM_FIELDS = Object.keys(blank());

  function startEdit(printer) {
    const next = { ...blank() };
    for (const key of FORM_FIELDS) {
      if (printer[key] != null) next[key] = printer[key];
    }
    next.rfidSettingsText = prettifyJson(printer.rfidSettings);
    setEditingId(printer.id);
    setForm(next);
    setError('');
    setStatus('');
    setFormOpen(true);
    analyze(next);
  }

  function cancelEdit() {
    setEditingId('');
    setForm(blank());
    setFormOpen(false);
    setAnalysis([]);
  }

  function round1(v) {
    return Math.round(v * 10) / 10;
  }

  function buildFindings(f, templates) {
    const dpi = f.dpi || 203;
    const linterMaxMm = round1((799 * 25.4) / dpi);
    const eff = f.maxPrintWidthMm > 0 ? f.maxPrintWidthMm : null;
    const findings = [];

    if (eff == null) {
      findings.push({
        level: 'warn',
        text: `Largura útil não definida: o linter limita ^PW a 799 dots (≈ ${linterMaxMm} mm a ${dpi} dpi). Defina um valor.`,
      });
    } else if (eff > linterMaxMm) {
      findings.push({
        level: 'warn',
        text: `Largura útil ${eff} mm excede o limite do linter (${linterMaxMm} mm a ${dpi} dpi): o ^PW gerado será sinalizado.`,
      });
    } else {
      findings.push({ level: 'ok', text: `Largura útil ${eff} mm dentro do limite do linter (${linterMaxMm} mm).` });
    }

    const related = templates.filter((t) => t.printer?.id === f.id);
    if (related.length === 0) {
      findings.push({ level: 'ok', text: 'Nenhuma etiqueta vinculada a esta impressora ainda.' });
      return findings;
    }

    for (const t of related) {
      const lf = t.labelFormat;
      if (!lf) continue;
      const cols = lf.columns || 1;
      const colW = lf.columnWidthMm || lf.widthMm || 0;
      const content = round1(cols * colW + (cols - 1) * (lf.horizontalGapMm || 0));
      const printable = eff ?? (lf.paperWidthMm || content);
      if (content > printable) {
        findings.push({
          level: 'error',
          text: `Etiqueta "${t.name}": conteúdo ${content} mm > área útil ${printable} mm — ${round1(content - printable)} mm da última coluna saem cortados. Reduza colunas ou largura no formato.`,
        });
      } else {
        findings.push({ level: 'ok', text: `Etiqueta "${t.name}": ${content} mm cabe na área útil de ${printable} mm.` });
      }
    }
    return findings;
  }

  async function analyze(f) {
    setAnalyzing(true);
    setAnalysis([]);
    try {
      const templates = await api.listTemplates();
      setAnalysis(buildFindings(f, templates));
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  }

  async function toggleStatus(printer) {
    try {
      await api.updatePrinter(printer.id, { status: printer.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(printer) {
    if (!confirm(`Remover a impressora "${printer.name}"?`)) return;
    try {
      await api.deletePrinter(printer.id);
      setStatus('Impressora removida.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="flex flex-col gap-base">
      <div>
        <h1 className="studio-uniform-title font-h2 text-h2 text-on-surface">Impressoras</h1>
      </div>

      {error && <p className="text-[11px] text-error">{error}</p>}
      {status && <p className="rounded-2xl border border-primary/10 bg-primary-container/40 px-4 py-3 text-[10px] text-on-primary-container">{status}</p>}

      <section className="bg-surface rounded-2xl shadow-sm p-6 flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">Detecção de impressoras</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!agentOnline && (
              <span className="inline-flex items-center gap-1.5 font-label-sm text-label-sm text-on-surface-variant">
                <span className="w-2 h-2 rounded-full bg-on-surface-variant/40" />
                Print Agent offline
              </span>
            )}
            {!agentOnline ? (
              <>
                <button
                  type="button"
                  onClick={connectAgent}
                  aria-disabled={agentConnecting}
                  className={`ml-1 inline-flex min-w-[124px] items-center justify-center rounded-full border border-primary/40 px-3 py-1 text-[10px] font-semibold text-primary transition-colors ${agentConnecting ? 'opacity-50 cursor-pointer' : 'hover:bg-primary/10 cursor-pointer'
                    }`}
                >
                  <div className="flex items-center gap-1.5">
                    {agentConnecting && <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>}
                    {agentConnecting ? 'Conectando...' : 'Conectar agent'}
                  </div>
                </button>
                <a href="etiquetandapp://parear=studio" className="rounded-full border border-primary/40 px-3 py-1 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/10">
                  Abrir Print Agent
                </a>
              </>
            ) : (
              <button
                type="button"
                onClick={searchAgent}
                disabled={agentBusy}
                className="bg-primary hover:bg-surface-tint text-on-primary font-label-sm text-label-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-primary/20 disabled:opacity-50"
              >
                {agentBusy ? (
                  <>
                    <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
                    Buscando...
                  </>
                ) : (
                  'Buscar via Print Agent'
                )}
              </button>
            )}
            <button
              type="button"
              onClick={openBrowserPrintDialog}
              className="rounded-lg border border-surface-container-high px-4 py-2 text-[11px] font-medium text-on-surface"
            >
              Janela de impressão do navegador
            </button>
            <a
              className="ml-1 rounded-full border border-surface-container-highest bg-surface-container-low px-3 py-1 text-[10px] font-semibold text-on-surface-variant transition-colors hover:text-on-surface"
              href="#/baixar-app"
            >
              Baixar App
            </a>
          </div>
        </div>

        {(discovered.windows.length > 0 || discovered.serial.length > 0 || discovered.network.length > 0) && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px]">
                <thead>
                  <tr className="border-b border-surface-container-high text-[10px] uppercase tracking-wide text-on-surface-variant">
                    <th className="py-2 pr-4 font-semibold">Impressora</th>
                    <th className="py-2 pr-4 font-semibold">Porta</th>
                    <th className="py-2 pr-4 font-semibold">Status</th>
                    <th className="py-2 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {detectPageRows.map((row) => (
                    <tr key={row.key} className="border-b border-surface-container-low">
                      <td className="py-2 pr-4 font-medium text-on-surface">{row.name}</td>
                      <td className="py-2 pr-4 text-on-surface-variant">{row.port}</td>
                      <td className="py-2 pr-4">
                        {row.registered ? (
                          <span className="inline-flex items-center gap-1.5 text-primary">
                            <span className="w-2 h-2 rounded-full bg-primary" /> Cadastrada
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-error">
                            <span className="w-2 h-2 rounded-full bg-error" /> Não cadastrada
                          </span>
                        )}
                      </td>
                      <td className="py-2">
                        {row.registered ? (
                          <div className="flex gap-2">
                            <button type="button" onClick={() => startEdit(row.registered)} className="rounded-lg border border-surface-container-high px-2.5 py-1 text-[10px] font-medium text-on-surface">
                              Editar
                            </button>
                            <button type="button" onClick={() => remove(row.registered)} className="rounded-lg border border-error/20 bg-error-container/50 px-2.5 py-1 text-[10px] font-medium text-error">
                              Excluir
                            </button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => openRegisterForm(row.name, row.comm)} className="rounded-lg border border-primary/40 px-2.5 py-1 text-[10px] font-medium text-primary">
                            Cadastrar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {detectRows.length > 5 && (
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-on-surface-variant">
                  {detectRows.length} impressoras · página {detectPage + 1} de {detectPageCount}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={detectPage === 0}
                    onClick={() => setDetectPage((p) => Math.max(0, p - 1))}
                    className="rounded-lg border border-surface-container-high px-3 py-1 text-[10px] font-medium text-on-surface disabled:opacity-40"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={detectPage >= detectPageCount - 1}
                    onClick={() => setDetectPage((p) => Math.min(detectPageCount - 1, p + 1))}
                    className="rounded-lg border border-surface-container-high px-3 py-1 text-[10px] font-medium text-on-surface disabled:opacity-40"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {awaitingPrinterName && (
        <form onSubmit={confirmCapturedName} className="bg-surface rounded-2xl shadow-sm p-6 flex flex-col gap-4">
          <div>
            <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">Impressora escolhida no navegador</h3>
            <p className="text-[11px] text-on-surface-variant mt-1">
              O navegador não informa o nome ao site. Digite abaixo o nome da impressora que você selecionou.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[...new Set([...commonPrinterNames, ...discovered.windows.map((p) => p.name)])].map((name) => (
              <button key={name} type="button" onClick={() => pickPrinterName(name)} className="rounded-lg border border-surface-container-high px-3 py-1.5 text-[10px] font-medium text-on-surface">
                {name}
              </button>
            ))}
          </div>
          {pendingPrinterName && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/20 bg-primary-container/40 px-4 py-3">
              <span className="text-[11px] text-on-surface">Cadastrar a impressora "{pendingPrinterName}"?</span>
              <button type="button" onClick={confirmPendingPrinter} className="bg-primary hover:bg-surface-tint text-on-primary font-label-sm text-label-sm px-4 py-2 rounded-lg">
                Sim, cadastrar
              </button>
              <button type="button" onClick={() => setPendingPrinterName(null)} className="rounded-lg border border-surface-container-high px-4 py-2 text-[11px] font-medium text-on-surface">
                Não
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <input
              value={capturedName}
              onChange={(e) => setCapturedName(e.target.value)}
              placeholder="Nome da impressora (ex.: Zebra ZT230)"
              className="flex-1 rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
            <button type="submit" className="bg-primary hover:bg-surface-tint text-on-primary font-label-sm text-label-sm px-6 py-3 rounded-lg transition-all shadow-md shadow-primary/20">
              Usar
            </button>
            <button type="button" onClick={() => setAwaitingPrinterName(false)} className="px-4 py-3 rounded-lg border border-surface-container-high text-[11px] font-medium text-on-surface">
              Cancelar
            </button>
          </div>
        </form>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={() => cancelEdit()}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-surface rounded-2xl shadow-sm p-6 flex flex-col gap-4 w-full max-w-3xl max-h-full overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">{editingId ? 'Editar impressora' : 'Nova impressora'}</h3>
                <p className="text-[11px] text-on-surface-variant mt-1">Cadastre o perfil de hardware para reutilizar em vários trabalhos.</p>
              </div>
              <button type="button" onClick={cancelEdit} className="rounded-full p-2 text-on-surface-variant hover:bg-surface-container-low">
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>
            <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-4">
              <label className="flex flex-col gap-2 md:col-span-1">
                <span className="text-[10px] font-semibold text-on-surface-variant">Nome</span>
                <input value={form.name} onChange={(e) => update('name', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-[10px] font-semibold text-on-surface-variant">Fabricante</span>
                <input value={form.manufacturer} onChange={(e) => update('manufacturer', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-[10px] font-semibold text-on-surface-variant">Modelo</span>
                <input value={form.model} onChange={(e) => update('model', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
              </label>
              <label className="flex flex-col gap-2">
                <span className="text-[10px] font-semibold text-on-surface-variant">Linguagem</span>
                <input value={form.language} onChange={(e) => update('language', e.target.value)} placeholder="ZPL" className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
              </label>
              <NumberInput label="DPI" value={form.dpi} onChange={(value) => update('dpi', value)} />
              <NumberInput label="Velocidade (IPS)" step="0.5" value={form.defaultSpeedIps} onChange={(value) => update('defaultSpeedIps', value)} />
              <NumberInput label="Densidade" value={form.defaultDensity} onChange={(value) => update('defaultDensity', value)} />
              <SelectInput label="Orientação" value={form.orientation} options={orientationOptions} onChange={(value) => update('orientation', value)} />
              <NumberInput label="Ajuste horizontal (mm)" step="0.1" value={form.horizontalAdjustmentMm} onChange={(value) => update('horizontalAdjustmentMm', value)} />
              <NumberInput label="Ajuste vertical (mm)" step="0.1" value={form.verticalAdjustmentMm} onChange={(value) => update('verticalAdjustmentMm', value)} />
              <NumberInput label="Largura útil de impressão (mm)" step="0.1" value={form.maxPrintWidthMm} onChange={(value) => update('maxPrintWidthMm', value)} />
              <SelectInput label="Sensor" value={form.sensorType} options={sensorOptions} onChange={(value) => update('sensorType', value)} />
              <SelectInput label="Corte" value={form.cutMode} options={cutOptions} onChange={(value) => update('cutMode', value)} />
              <NumberInput label="Retrocesso (dots)" value={form.backfeedDots} onChange={(value) => update('backfeedDots', value)} />
              <SelectInput label="Método de incremento" value={form.incrementMode} options={incrementOptions} onChange={(value) => update('incrementMode', value)} />
              <SelectInput label="Momento da fonte de dados" value={form.dataSourceTrigger} options={dataSourceTriggerOptions} onChange={(value) => update('dataSourceTrigger', value)} />
              <SelectInput label="Comunicação" value={form.communicationType} options={commOptions} onChange={(value) => update('communicationType', value)} />
              <label className="flex items-center gap-3 rounded-2xl border border-surface-container-high px-4 py-3 text-[10px] text-on-surface">
                <input type="checkbox" checked={!!form.mirrored} onChange={(e) => update('mirrored', e.target.checked)} />
                Espelhar horizontalmente
              </label>
            </div>
            <div>
              <label className="flex flex-col gap-2">
                <span className="text-[10px] font-semibold text-on-surface-variant">Configuração RFID (JSON)</span>
                <textarea
                  value={form.rfidSettingsText}
                  onChange={(e) => update('rfidSettingsText', e.target.value)}
                  rows={4}
                  className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 font-mono text-[10px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
                />
              </label>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-on-surface-variant">Análise de compatibilidade</span>
                <button type="button" onClick={() => analyze(form)} disabled={analyzing} className="rounded-lg border border-surface-container-high px-3 py-1.5 text-[10px] font-medium text-on-surface disabled:opacity-50">
                  {analyzing ? 'Analisando...' : 'Analisar'}
                </button>
              </div>
              {analysis.length > 0 && (
                <ul className="flex flex-col gap-1.5">
                  {analysis.map((a, i) => (
                    <li key={i} className={`rounded-xl border px-3 py-2 text-[10px] ${a.level === 'error'
                      ? 'border-error/30 bg-error-container/40 text-error'
                      : a.level === 'warn'
                        ? 'border-amber-500/30 bg-amber-500/10 text-amber-700'
                        : 'border-primary/20 bg-primary-container/40 text-on-primary-container'
                      }`}>
                      {a.text}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-primary hover:bg-surface-tint text-on-primary font-label-sm text-label-sm px-6 py-3 rounded-lg transition-all shadow-md shadow-primary/20">
                {editingId ? 'Salvar alterações' : 'Cadastrar impressora'}
              </button>
              <button type="button" onClick={cancelEdit} className="px-6 py-3 rounded-lg border border-surface-container-high text-[11px] font-medium text-on-surface">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {canManage && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              setEditingId('');
              setForm(blank());
              setError('');
              setStatus('');
              setFormOpen(true);
            }}
            className="bg-primary hover:bg-surface-tint text-on-primary font-label-sm text-label-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-primary/20"
          >
            Nova impressora
          </button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-on-surface-variant">{list.length} dispositivos</span>
        <div className="flex rounded-lg border border-surface-container-high p-0.5 text-[10px] font-medium">
          <button
            type="button"
            onClick={() => setViewMode('table')}
            className={`rounded-md px-3 py-1.5 transition-colors ${viewMode === 'table' ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:text-on-surface'}`}
          >
            Tabela
          </button>
          <button
            type="button"
            onClick={() => setViewMode('card')}
            className={`rounded-md px-3 py-1.5 transition-colors ${viewMode === 'card' ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:text-on-surface'}`}
          >
            Card
          </button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <div className="overflow-x-auto bg-surface rounded-2xl shadow-sm">
          <table className="w-full text-left text-[11px]">
            <thead>
              <tr className="border-b border-surface-container-high text-[10px] uppercase tracking-wide text-on-surface-variant">
                <th className="py-3 pr-4 pl-6 font-semibold">Impressora</th>
                <th className="py-3 pr-4 font-semibold">Fabricante/Modelo</th>
                <th className="py-3 pr-4 font-semibold">DPI</th>
                <th className="py-3 pr-4 font-semibold">Comunicação</th>
                <th className="py-3 pr-4 font-semibold">Trabalhos</th>
                <th className="py-3 pr-4 font-semibold">Status</th>
                <th className="py-3 pr-6 font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map((printer) => (
                <tr key={printer.id} className="border-b border-surface-container-low">
                  <td className="py-3 pr-4 pl-6 font-medium text-on-surface">{printer.name}</td>
                  <td className="py-3 pr-4 text-on-surface-variant">{[printer.manufacturer, printer.model].filter(Boolean).join(' ') || '—'}</td>
                  <td className="py-3 pr-4 text-on-surface">{printer.dpi}</td>
                  <td className="py-3 pr-4 text-on-surface">{printer.communicationType}</td>
                  <td className="py-3 pr-4 text-on-surface">{printer._count?.printJobs ?? 0}</td>
                  <td className="py-3 pr-4">
                    <span className={`inline-flex items-center gap-1.5 font-label-sm text-label-sm ${printer.status === 'ACTIVE' ? 'text-primary' : 'text-error'}`}>
                      <span className={`w-2 h-2 rounded-full ${printer.status === 'ACTIVE' ? 'bg-primary' : 'bg-error'}`} />
                      {printer.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="py-3 pr-6">
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(printer)} className="rounded-lg border border-surface-container-high px-2.5 py-1 text-[10px] font-medium text-on-surface">
                        Editar
                      </button>
                      {canManage && (
                        <>
                          <button onClick={() => toggleStatus(printer)} className="rounded-lg border border-surface-container-high px-2.5 py-1 text-[10px] font-medium text-on-surface">
                            {printer.status === 'ACTIVE' ? 'Desativar' : 'Ativar'}
                          </button>
                          <button onClick={() => remove(printer)} className="rounded-lg border border-error/20 bg-error-container/50 px-2.5 py-1 text-[10px] font-medium text-error">
                            Excluir
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-3">
          {list.length === 0 ? (
            <p className="col-span-full text-[11px] text-on-surface-variant">
              Nenhum dispositivo cadastrado ainda.
            </p>
          ) : (
            list.map((printer) => (
              <div key={printer.id} className="bg-surface rounded-2xl shadow-sm p-6 flex flex-col gap-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-xl bg-primary-container text-on-primary-container flex items-center justify-center">
                    <span className="material-symbols-outlined">print</span>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 font-label-sm text-label-sm ${printer.status === 'ACTIVE' ? 'text-primary' : 'text-error'}`}>
                    <span className={`w-2 h-2 rounded-full ${printer.status === 'ACTIVE' ? 'bg-primary' : 'bg-error'}`} />
                    {printer.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div>
                  <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">{printer.name}</h3>
                  <p className="text-[11px] text-on-surface-variant mt-1">
                    {[printer.manufacturer, printer.model].filter(Boolean).join(' ') || 'Sem fabricante/modelo'} · {printer.language || '—'}
                  </p>
                </div>
                <div className="mt-auto flex flex-wrap gap-2 text-[10px]">
                  <span className="rounded-full bg-surface-container-low px-3 py-1 text-on-surface-variant">{printer.dpi} DPI</span>
                  <span className="rounded-full bg-surface-container-low px-3 py-1 text-on-surface-variant">{printer.communicationType}</span>
                  <span className="rounded-full bg-surface-container-low px-3 py-1 text-on-surface-variant">{printer._count?.printJobs ?? 0} trabalhos</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(printer)} className="rounded-xl border border-surface-container-high px-3 py-2 text-[10px] font-medium text-on-surface">
                    Editar
                  </button>
                  {canManage && (
                    <>
                      <button onClick={() => toggleStatus(printer)} className="rounded-xl border border-surface-container-high px-3 py-2 text-[10px] font-medium text-on-surface">
                        {printer.status === 'ACTIVE' ? 'Desativar' : 'Ativar'}
                      </button>
                      <button onClick={() => remove(printer)} className="rounded-xl border border-error/20 bg-error-container/50 px-3 py-2 text-[10px] font-medium text-error">
                        Excluir
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
